import React, { useState, useEffect } from "react";
import Loader from "../../../../components/Loader/Loader";
import Header from "../../../../components/Header";
import Footer from "../../../../components/Footer";
import OrderDetails from "../OrderDetails";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import { HelmetProvider } from "react-helmet-async";
import MetaTags from "../../../../components/MetaTags";
import useExperienceData from "../../../../hooks/useExperienceData";
import useCheckoutContext from "../../../../hooks/useExperienceCheckout";
import MercadoPago from "../../../Checkout/Components/payments/MercadoPago";
import Rebill from "../../../Checkout/Components/payments/Rebill";
import { PAYMENT_PROVIDERS } from "../../../../constants/paymentProviders";
import { getPaymentProviders } from "../../../../services/paymentProvidersService";
import { PaymentProvider } from "../../../../types/paymentProviders";
import { useLocation, useNavigate } from "react-router-dom";
import whatsapp_icon from "../../../../assets/icons/whatsapp_green.svg";

const Payments = () => {
    const currentStep = 2;

    const [loading] = useState(false);
    const [paymentProvider, setPaymentProvider] = useState("");
    const [availablePaymentProviders, setAvailablePaymentProviders] = useState<PaymentProvider[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Get order from checkout context
    const { order, loading: orderLoading, error } = useCheckoutContext();
    const { experience, promoter } = useExperienceData();

    // Fetch available payment providers
    useEffect(() => {
        const fetchPaymentProviders = async () => {
            try {
                const providers = await getPaymentProviders();
                setAvailablePaymentProviders(providers);
            } catch (e) {
                console.error("Error fetching payment providers:", e);
                setAvailablePaymentProviders([]);
            }
        };
        
        fetchPaymentProviders();
    }, []);

    // Move navigation logic into useEffect to avoid setState in render
    useEffect(() => {
        if (error && error.message === 'Order not found') {
            // Extract the experience slug from the current path
            const match = location.pathname.match(/^\/experiences\/([^/]+)/);
            const experienceSlug = match ? match[1] : '';
            if (experienceSlug) {
                navigate(`/experiences/${experienceSlug}`);
            } else {
                navigate('/');
            }
        }
    }, [error, location.pathname, navigate]);

    // Helper function to check if a payment provider is available
    const isProviderAvailable = (code: string) => {
        return availablePaymentProviders.some(provider => provider.code === code);
    };

    // Show WhatsApp if no payment providers
    const noPaymentProviders = availablePaymentProviders.length === 0;

    // WhatsApp handler for no payment providers
    const handlePhoneClick = () => {
        if (!order?.booking?.public_id) return;
        const message = encodeURIComponent(
            `Hola, escribo porque necesito un link de pago para el booking ${order.booking.public_id}.`
        );
        window.open(`https://wa.me/5491127516586?text=${message}`, "_blank")?.focus();
    };

    // Determine if Rebill is the only available provider
    const isOnlyRebill =
        availablePaymentProviders.length === 1 &&
        (availablePaymentProviders[0].code === PAYMENT_PROVIDERS.REBILL || availablePaymentProviders[0].code === PAYMENT_PROVIDERS.REBILL_US);

    // Determine if Rebill is selected
    const isRebillSelected = paymentProvider === PAYMENT_PROVIDERS.REBILL || paymentProvider === PAYMENT_PROVIDERS.REBILL_US;

    if (loading || orderLoading || !experience || !promoter || !order) return <Loader />;

    const currentUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + window.location.search

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter?.fullname || ""}
                icon={promoter?.logoURL || ""}
                url={currentUrl}
                font={experience.font ? experience.font : null}
                primaryColor={experience.colors.primary_color ? experience.colors.primary_color : null}
                secondaryColor={experience.colors.secondary_color ? experience.colors.secondary_color : null}
            />
            <Header loading={false} promoter={promoter} stock={Infinity} error={null}></Header>
            <div className="payments__container">
                <Breadcrumbs currentStep={currentStep} orderStatus={null} noGoBack={true}></Breadcrumbs>
                <div className="main-content">
                    <div className="payments-details__container">
                        {/* Only render MercadoPago if it's available */}
                        {isProviderAvailable(PAYMENT_PROVIDERS.MERCADO_PAGO) && (
                            <MercadoPago order={order} isExtenalProduct={true} />
                        )}
                        
                        {/* Only render Rebill if it's available */}
                        {isProviderAvailable(PAYMENT_PROVIDERS.REBILL) && (
                            <Rebill 
                                setPaymentProvider={setPaymentProvider} 
                                paymentProvider={paymentProvider}
                                order={order}
                                isExperience={true}
                                isOnlyProvider={isOnlyRebill}
                            />
                        )}
                        
                        {/* Only render Rebill US if it's available */}
                        {isProviderAvailable(PAYMENT_PROVIDERS.REBILL_US) && (
                            <Rebill 
                                setPaymentProvider={setPaymentProvider} 
                                paymentProvider={paymentProvider}
                                order={order}
                                isExperience={true}
                                isOnlyProvider={isOnlyRebill}
                                isUsProvider={true}
                            />
                        )}
                        {/* Show WhatsApp link if no payment providers */}
                        {noPaymentProviders && (
                            <div className="no-payment-providers-whatsapp" style={{ margin: "24px 0" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <img src={whatsapp_icon} alt="WhatsApp" style={{ width: 24, height: 24 }} />
                                    <span>
                                        Lo sentimos, no contamos con medios de pago online para tu país.<br />
                                        Por favor,{" "}
                                        <span
                                            style={{ color: "#25D366", textDecoration: "underline", cursor: "pointer" }}
                                            onClick={handlePhoneClick}
                                        >
                                            contáctanos
                                        </span>
                                        {" "}para poder ofrecerte alternativas y concretar la compra.
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    <OrderDetails
                        handleCheckboxChange={() => { alert("Not implemented") }}
                        handleSubmitNextStep={() => { alert("Not implemented") }}
                        isFormValid={true}
                        nextStepLabel={"Siguiente paso"}
                        isOnlyRebill={isOnlyRebill}
                        isRebillSelected={isRebillSelected}
                    />
                </div>
            </div>
            <Footer loading={false} promoter={promoter}></Footer>
        </HelmetProvider>
    );
};

export default Payments;
